import { getEnvVar } from "./env";

// Type definitions for Google Analytics
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    gaInitialized?: boolean;
  }
}

/**
 * Initialize Google Analytics 4
 * Uses VITE_GA_ID or VITE_GA_MEASUREMENT_ID environment variable
 */
export const initGA = (): void => {
  if (typeof window === "undefined") return;

  // Each app must set its own Measurement ID (GA4 Admin > Data Streams). No shared fallback.
  const measurementId =
    getEnvVar("VITE_GA_ID") || getEnvVar("VITE_GA_MEASUREMENT_ID");

  if (!measurementId) {
    console.warn("GA Measurement ID not found");
    return;
  }

  // Check if already initialized
  if (window.gaInitialized) {
    return;
  }

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];

  function gtag(...args: unknown[]) {
    window.dataLayer.push(args);
  }

  window.gtag = gtag;

  // Set consent defaults
  gtag("consent", "default", {
    analytics_storage: "granted",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });

  // Load GA4 script
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  script.onload = () => {
    window.gtag("js", new Date());
    window.gtag("config", measurementId, {
      anonymize_ip: true,
      cookie_flags: "SameSite=None;Secure",
      // Page views come only from trackPageView (one per route, pathname-only);
      // the SSR snippet configures with send_page_view: false too.
      send_page_view: false,
    });

    window.gaInitialized = true;
  };

  script.onerror = () => {
    console.error("Failed to load Google Analytics script");
  };
};

/**
 * Track page view in Google Analytics
 */
export const trackPageView = (path: string, title: string): void => {
  if (typeof window === "undefined" || !window.gtag) return;

  const measurementId =
    getEnvVar("VITE_GA_ID") || getEnvVar("VITE_GA_MEASUREMENT_ID");
  if (!measurementId) return;

  try {
    // `config` here would count a page view per call on top of any automatic
    // one; a single explicit event is the only page view. Query strings can
    // hold user-entered finder text, so only the pathname is sent.
    const safePath = new URL(path, window.location.origin).pathname;
    window.gtag("event", "page_view", {
      page_path: safePath,
      page_location: `${window.location.origin}${safePath}`,
      page_title: title,
    });
  } catch (error) {
    console.error("Error tracking page view:", error);
  }
};
